import type { TileState } from '../tileManager';
import { TILE_SIZE } from '../../gen/config';

/** Colours of the radar: dark ground, pale blocks, darker buildings, green parks, bright roads. */
const C = {
  ground: '#1d2622',
  block: '#3a4148',
  building: '#262b31',
  park: '#2f5a37',
  road: '#c9ced6',
  main: '#f2c14e',
  rim: 'rgba(255,255,255,0.22)',
};

/**
 * A GTA-style radar, bottom left: the city around the player drawn from the tiles' plans (blocks,
 * building footprints, parks, the road graph), heading-up, zooming out with speed. Redrawn ten times
 * a second into a small canvas; the tiles near the player are the only ones drawn.
 */
export class Minimap {
  readonly el: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly north: HTMLDivElement;
  private last = 0;
  /** Metres from the centre to the rim, eased toward the wanted zoom. */
  private range = 140;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'minimap';
    this.canvas = document.createElement('canvas');
    this.el.appendChild(this.canvas);
    this.north = document.createElement('div');
    this.north.className = 'north';
    this.north.textContent = 'N';
    this.el.appendChild(this.north);
    const arrow = document.createElement('div');
    arrow.className = 'me';
    this.el.appendChild(arrow);
    parent.appendChild(this.el);
    this.ctx = this.canvas.getContext('2d')!;
  }

  set visible(on: boolean) {
    this.el.style.display = on ? '' : 'none';
  }

  /**
   * Draw around (gx, gy) (generation cm) with the view heading `yaw` (the fly camera's convention:
   * forward is (-sin yaw, cos yaw) in generation x, y). `want` is the range in metres.
   */
  update(now: number, tiles: Iterable<TileState>, gx: number, gy: number, yaw: number, want: number): void {
    if (this.el.style.display === 'none' || now - this.last < 100) return;
    const dt = Math.min(0.5, (now - this.last) / 1000);
    this.last = now;
    this.range += (want - this.range) * Math.min(1, dt * 2);
    const css = this.canvas.clientWidth || 190;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const px = Math.round(css * dpr);
    if (this.canvas.width !== px) {
      this.canvas.width = px;
      this.canvas.height = px;
    }
    const ctx = this.ctx;
    const r = px / 2;
    const k = r / (this.range * 100); // pixels per cm
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = C.ground;
    ctx.fillRect(0, 0, px, px);
    // Generation space, heading up: y is flipped (canvas y points down) and the view's forward turned
    // to point up the canvas.
    const fwd = Math.atan2(Math.cos(yaw), -Math.sin(yaw));
    ctx.setTransform(1, 0, 0, 1, r, r);
    ctx.rotate(fwd - Math.PI / 2);
    ctx.scale(k, -k);
    ctx.translate(-gx, -gy);
    const reach = this.range * 100 * 1.5;
    const near: TileState[] = [];
    for (const t of tiles) {
      const cx = t.tx * TILE_SIZE;
      const cy = t.ty * TILE_SIZE;
      if (Math.abs(cx - gx) > TILE_SIZE / 2 + reach || Math.abs(cy - gy) > TILE_SIZE / 2 + reach) continue;
      near.push(t);
    }
    const poly = (p: number[]) => {
      ctx.beginPath();
      ctx.moveTo(p[0], p[1]);
      for (let i = 2; i < p.length; i += 2) ctx.lineTo(p[i], p[i + 1]);
      ctx.closePath();
    };
    const inView = (p: number[]) => {
      // Cheap reject: first vertex farther than the reach plus a block.
      return Math.abs(p[0] - gx) < reach + 30000 && Math.abs(p[1] - gy) < reach + 30000;
    };
    ctx.fillStyle = C.block;
    for (const t of near) {
      for (const b of t.blocks ?? []) {
        if (!inView(b.polygon)) continue;
        poly(b.polygon);
        ctx.fill();
      }
    }
    for (const t of near) {
      for (const b of t.buildings ?? []) {
        if (!inView(b.footprint)) continue;
        ctx.fillStyle = b.kind === 'park' ? C.park : C.building;
        poly(b.footprint);
        ctx.fill();
      }
    }
    ctx.lineCap = 'round';
    for (const main of [false, true]) {
      ctx.strokeStyle = main ? C.main : C.road;
      ctx.lineWidth = main ? 1100 : 700;
      ctx.beginPath();
      for (const t of near) {
        const g = t.roads?.graph;
        if (!g) continue;
        for (let i = 0; i < g.edges.length / 2; i++) {
          if ((g.types[i] === 0) !== main) continue;
          const a = g.edges[i * 2];
          const b = g.edges[i * 2 + 1];
          ctx.moveTo(g.nodes[a * 2], g.nodes[a * 2 + 1]);
          ctx.lineTo(g.nodes[b * 2], g.nodes[b * 2 + 1]);
        }
      }
      ctx.stroke();
    }
    // The north marker rides the rim: generation +y lands at canvas angle fwd - π.
    const rim = css / 2 - 11;
    const nx = css / 2 + Math.cos(fwd - Math.PI) * rim;
    const ny = css / 2 + Math.sin(fwd - Math.PI) * rim;
    this.north.style.transform = `translate(${(nx - 7).toFixed(1)}px, ${(ny - 8).toFixed(1)}px)`;
  }
}
